/* eslint-disable no-console */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';

import { useCurrentUser } from '../hooks/useCurrentUser';
import { useOrganizationSelector } from '../hooks/useOrganizationSelector';
import useReferralProviderData from '../hooks/useReferralProviderData';
import { useSearchJobsQuery } from '../redux/apis/jobsApi';
import {
  organizationApi,
  useGetCompanyProfileQuery,
  useGetOrganizationReferralFormPermissionsQuery,
  useGetOrganizationStrengthenQuestionsQuery,
} from '../redux/apis/organizationApi';
import { useGetSelectedContactQuery } from '../redux/apis/referralApi';
import { useGetCurrentUserQuery, userApi } from '../redux/apis/userApi';
import { selectCurrentOrganization } from '../selectors/organizationSelectors';

const ReferralFlowContext = createContext();

const STORAGE_KEY = 'referralFlowData';

const saveToStorage = (data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save referral flow data to localStorage:', error);
  }
};

const loadFromStorage = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Failed to load referral flow data from localStorage:', error);
    return null;
  }
};

const clearStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear referral flow data from localStorage:', error);
  }
};

export const ReferralFlowProvider = ({ children }) => {
  const { isAuthenticated } = useCurrentUser();
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(1);
  const [personalInfo, setPersonalInfo] = useState();
  const [candidateInfo, setCandidateInfo] = useState();
  const [jobInfo, setJobInfo] = useState();
  const [reviewInfo, setReviewInfo] = useState();
  const [organizationId, setOrganizationId] = useState(null);
  const [isStoredDataLoaded, setIsStoredDataLoaded] = useState(false);
  const { data: userData } = useGetCurrentUserQuery(undefined, { skip: !isAuthenticated });
  const { companyName } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const openingId = searchParams.get('refer_for');
  const contactId = searchParams.get('c_id');
  const { data: selectedContact } = useGetSelectedContactQuery({ contactId }, { skip: !contactId || !isAuthenticated });
  const { data: companyProfile } = useGetCompanyProfileQuery(companyName, {
    skip: !companyName,
  });
  const companyOrganization = companyProfile?.payload?.organization;
  const currentOrganization = useSelector(selectCurrentOrganization);
  const organization = companyOrganization || currentOrganization;
  const accessOrganization = useMemo(() => {
    const selectedId = organizationId || jobInfo?.company;
    if (selectedId) {
      return { id: Number(selectedId) };
    }
    return organization;
  }, [organizationId, jobInfo?.company, organization]);
  const { referralProvider } = useReferralProviderData();
  const { data: jobs } = useSearchJobsQuery(
    { searchTerm: '', organizationId: organization?.id, rp: referralProvider?.user?.slug },
    { skip: !organization?.id || !openingId }
  );
  const { data: organizationStrengthenQuestions } = useGetOrganizationStrengthenQuestionsQuery(
    {
      organizationId: organizationId || organization?.id,
    },
    { skip: !(organizationId || organization?.id) }
  );

  const { data: organizationReferralFormPermissions } = useGetOrganizationReferralFormPermissionsQuery(
    {
      organizationId: organizationId || organization?.id,
    },
    { skip: !(organizationId || organization?.id) }
  );

  const referralFormPermissions = organizationReferralFormPermissions?.payload || {
    add_referral_form_custom_questions: false,
    linkedin_url_required: false,
    phone_number_required: false,
    resume_required: false,
    candidate_referral_received_notification: true,
    show_message_to_candidate: true,
    show_message_to_employer: true,
  };

  const strengthenQuestions = organizationStrengthenQuestions?.payload || {
    attach_file: true,
    attach_url: true,
    diversity_hire: true,
    enable_referral_questions: true,
    how_good_are_they: true,
    know_the_person_level: true,
    open_for_new_opportunities_status: true,
    worked_with_person: true,
  };

  useEffect(() => {
    if (selectedContact?.payload) {
      setCandidateInfo(selectedContact.payload);
      if (
        !referralFormPermissions?.add_referral_form_custom_questions &&
        referralFormPermissions?.referral_form_custom_questions?.length === 0
      )
        setCurrentStep(4);
    }
  }, [selectedContact, referralFormPermissions]);

  useOrganizationSelector();
  const storedData = loadFromStorage();

  useEffect(() => {
    if (organization) {
      setJobInfo((prev) => ({
        ...prev,
        company: organization?.id,
      }));
      // Invalidate referral form permissions cache when organization changes
      dispatch(organizationApi.util.invalidateTags(['OrganizationReferralFormPermissions']));
    }
  }, [organization, dispatch]);

  useEffect(() => {
    if (jobs && openingId) {
      setJobInfo((prev) => ({
        ...prev,
        opening: jobs?.find((job) => job.uuid === openingId)?.id,
      }));
    }
  }, [jobs, openingId]);

  useEffect(() => {
    if (storedData) {
      setCurrentStep(storedData.currentStep || 1);
      setPersonalInfo(storedData.personalInfo);
      setCandidateInfo(storedData.candidateInfo);
      setJobInfo(storedData.jobInfo);
      setReviewInfo(storedData.reviewInfo);
    }
    setIsStoredDataLoaded(true);

    // Invalidate referral form permissions cache on component mount to ensure fresh data
    dispatch(organizationApi.util.invalidateTags(['OrganizationReferralFormPermissions']));
  }, [dispatch]);

  useEffect(() => {
    if (!isStoredDataLoaded) return;

    const dataToSave = {
      currentStep,
      personalInfo,
      candidateInfo,
      jobInfo,
      reviewInfo,
      companyName,
    };
    saveToStorage(dataToSave);
  }, [currentStep, personalInfo, candidateInfo, jobInfo, reviewInfo, isStoredDataLoaded]);

  useEffect(() => {
    if (isAuthenticated && userData?.payload?.user) {
      if (!personalInfo) {
        setPersonalInfo({
          firstName: userData?.payload?.user?.first_name,
          lastName: userData?.payload?.user?.last_name,
          email: userData?.payload?.user?.email,
          phone: userData?.payload?.user?.phone_number,
          avatar_thumb: userData?.payload?.user?.avatar_thumb,
        });
      }
      if (!jobInfo) {
        setJobInfo((prev) => ({
          ...prev,
          company: currentOrganization?.id,
        }));
      }
    }
  }, [userData, isAuthenticated, currentOrganization, personalInfo, jobInfo]);

  useEffect(() => {
    if (!isAuthenticated && !companyName && openingId) {
      searchParams.delete('refer_for');
      setSearchParams(searchParams);
    }
  }, [isAuthenticated, companyName, openingId, searchParams, setSearchParams]);

  const clearReferralFlowData = () => {
    setCurrentStep(1);
    setPersonalInfo(undefined);
    setCandidateInfo(undefined);
    setJobInfo((prev) => ({ ...prev, opening: undefined }));
    setReviewInfo(undefined);
    clearStorage();
    dispatch(userApi.util.invalidateTags(['User']));
    dispatch(organizationApi.util.invalidateTags(['OrganizationReferralFormPermissions']));
  };

  useEffect(() => {
    if (storedData?.companyName && companyName !== storedData?.companyName) {
      clearReferralFlowData();
    }
  }, [companyName, storedData?.companyName]);

  const updatePersonalInfo = (info) => {
    setPersonalInfo(info);
  };

  const updateCandidateInfo = (info) => {
    setCandidateInfo(info);
  };

  const updateJobInfo = (info) => {
    setJobInfo(info);
  };

  const updateReviewInfo = (info) => {
    setReviewInfo(info);
  };

  const updateCurrentStep = (step) => {
    setCurrentStep(step);
  };

  const updateOrganizationId = (id) => {
    setOrganizationId(id);
  };

  const generateReferralPayload = () => {
    const referrerName = `${personalInfo?.firstName || ''} ${personalInfo?.lastName || ''}`.trim();

    const referralFormCustomQuestions = [];
    if (
      referralFormPermissions.add_referral_form_custom_questions &&
      referralFormPermissions.referral_form_custom_questions
    ) {
      referralFormPermissions.referral_form_custom_questions.forEach((question) => {
        const fieldName = `custom_question_${question.id}`;
        const answer = candidateInfo?.[fieldName];

        if (answer !== undefined && answer !== null && answer !== '') {
          referralFormCustomQuestions.push({
            question: question.title,
            answer,
            fieldType: question.fieldType,
            custom_field_key: question.custom_field_key,
            form_type: 'referral_form',
          });
        }
      });
    }

    return {
      referrer_name: referrerName,
      referrer_first_name: personalInfo?.firstName || '',
      referrer_last_name: personalInfo?.lastName || '',
      referrer_email: personalInfo?.email || '',
      name: `${candidateInfo?.first_name || ''} ${candidateInfo?.last_name || ''}`.trim(),
      email: candidateInfo?.email || '',
      phone_number: candidateInfo?.phone_number || '',
      linkedin_link: candidateInfo?.linkedin_url || '',
      opening: `job_${jobInfo?.opening || ''}`,
      organization_id: jobInfo?.company || 88,
      password: '',
      user_contact_id: candidateInfo?.user_contact_id || null,
      first_name: candidateInfo?.first_name || '',
      last_name: candidateInfo?.last_name || '',
      job_id: jobInfo?.opening || null,
      origin_channel: companyName ? 'instant_referral_link' : 'user_web_app',
      referral_form_custom_questions: referralFormCustomQuestions,
      resume_files: candidateInfo?.resume_files || [],
    };
  };

  const values = useMemo(
    () => ({
      personalInfo,
      candidateInfo,
      jobInfo,
      reviewInfo,
      strengthenQuestions,
      organizationId,
      setPersonalInfo: updatePersonalInfo,
      setCandidateInfo: updateCandidateInfo,
      setJobInfo: updateJobInfo,
      setReviewInfo: updateReviewInfo,
      currentStep,
      setCurrentStep: updateCurrentStep,
      setOrganizationId: updateOrganizationId,
      clearReferralFlowData,
      generateReferralPayload,
      referralFormPermissions,
      organization,
      accessOrganization,
    }),
    [
      personalInfo,
      candidateInfo,
      jobInfo,
      reviewInfo,
      currentStep,
      strengthenQuestions,
      organizationId,
      referralFormPermissions,
      organization,
      accessOrganization,
    ]
  );

  return <ReferralFlowContext.Provider value={values}>{children}</ReferralFlowContext.Provider>;
};

export const useReferralFlowContext = () => useContext(ReferralFlowContext);
